import React, { useState, useEffect } from "react";
import "./Search.css";
import {
  Container,
  TextField,
  Typography,
  Box,
  Button,
  Link as MuiLink,
} from "@mui/material";
import { Link } from "react-router-dom";

// Video Modal
const VideoPlayer = ({ video, onClose }) => {
  if (!video) return null;

  const embedUrl = `https://www.youtube.com/embed/${video.videoId}?autoplay=1`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{video.title}</h2>
          <button onClick={onClose} className="close-button">
            <img src="/assets/black-x.png" alt="Close" />
          </button>
        </div>
        <div className="video-player">
          <iframe
            src={embedUrl}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {video.subtitle && <h4 className="modal-desc">{video.subtitle}</h4>}
      </div>
    </div>
  );
};

export default function SearchResults() {
  const [inputText, setInputText] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [posts, setPosts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Fetch data only once on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [postsRes, videosRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/posts`),
          fetch(`${process.env.REACT_APP_API_URL}/videos`),
        ]);

        const postsData = await postsRes.json();
        const videosData = await videosRes.json();

        setPosts(postsData);
        setVideos(videosData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // Filter when "Submit" is clicked
  const handleSearchSubmit = () => {
    const key = inputText.toLowerCase();

    const matchedPosts = posts.filter((post) =>
      post.title.toLowerCase().includes(key)
    );

    const matchedVideos = videos.filter((video) =>
      video.title.toLowerCase().includes(key)
    );

    setSearchTerm(inputText);
    setFilteredPosts(matchedPosts);
    setFilteredVideos(matchedVideos);
  };

  return (
    <Box className="search-content">
      <Container sx={{ py: 6, height: "100%" }}>
        <Typography variant="h4" align="center" gutterBottom>
          Search Library
        </Typography>

        {/* Search Form */}
        <Box display="flex" gap={2} mb={4}>
          <TextField
            fullWidth
            variant="outlined"
            label="Search by title"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
          <Button variant="contained" onClick={handleSearchSubmit}>
            Submit
          </Button>
        </Box>

        {/* Posts Section */}
        {searchTerm && (
          <>
            <Typography variant="h5" gutterBottom>
              Matching Posts
            </Typography>

            {filteredPosts.length > 0 ? (
              filteredPosts.map((post) => (
                <Box
                  key={post._id}
                  className="post-item"
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="h6" color="black">
                    {post.title}
                  </Typography>
                  {post.subtitle && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {post.subtitle}
                    </Typography>
                  )}
                  <MuiLink
                    component={Link}
                    to={`/posts/${post._id}`}
                    underline="none"
                  >
                    <Button variant="contained" className="custom-button">
                      Read Online
                    </Button>
                  </MuiLink>
                </Box>
              ))
            ) : (
              <Typography>No matching posts found.</Typography>
            )}

            {/* Videos Section */}
            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
              Matching Videos
            </Typography>

            {filteredVideos.length > 0 ? (
              <Box
                className="video-grid"
                sx={{
                  display: "grid",
                  gap: 3,
                  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                }}
              >
                {filteredVideos.map((video) => (
                  <Box
                    key={video._id}
                    className="video-item"
                    onClick={() => setSelectedVideo(video)}
                    sx={{
                      cursor: "pointer",
                      border: "1px solid #ccc",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`}
                      alt={video.title}
                      width="100%"
                    />
                    <Box sx={{ p: 1 }}>
                      <Typography color="black">{video.title}</Typography>
                      {video.instructor && (
                        <Typography variant="caption" color="text.secondary">
                          {video.instructor}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography>No matching videos found.</Typography>
            )}
          </>
        )}

        {/* Video Modal */}
        <VideoPlayer
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      </Container>
    </Box>
  );
}
